import { useEffect, useRef, useState } from 'react';
import { apiUrl, authHeaders } from '@/lib/shell';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { workspaceErrorCopy } from './workspace-errors';
import type { WorkspaceFindHit, WorkspaceFindKind, WorkspaceFindPage } from './types';

const PAGE_SIZE = 24;
const SKILL_PAGE_SIZE = 20;
const MAX_FIND_HITS = 200;
const FIND_PATH = '/api/v1/company/find';

/** An owner is `user\0company`: both must be resolved. An empty part means the session or company is not
 * committed yet, and a company request sent then goes out before the account guard exists. */
const ownerResolved = (owner: string) =>
  owner !== '' && owner.split('\0').every((part) => part !== '');

export async function findWorkspace(
  query: string,
  kinds: WorkspaceFindKind[],
  offset: number,
  signal?: AbortSignal,
  mode: 'hybrid' | 'keyword' = 'hybrid',
  locale?: string,
): Promise<WorkspaceFindPage> {
  const copy = workspaceErrorCopy(locale);
  const params = new URLSearchParams({
    query: query.trim(),
    ...(mode === 'keyword' ? { mode } : {}),
    kinds: kinds.join(','),
    offset: String(offset),
    limit: String(PAGE_SIZE),
  });
  const response = await fetch(apiUrl(`${FIND_PATH}?${params}`), {
    credentials: 'same-origin',
    headers: authHeaders(),
    signal,
  });
  if (!response.ok)
    throw new Error(response.status === 401 ? copy.search401 : copy.searchUnavailable);
  const value = (await response.json()) as WorkspaceFindPage;
  if (
    !value ||
    !Array.isArray(value.hits) ||
    value.hits.length > PAGE_SIZE ||
    typeof value.hasMore !== 'boolean'
  )
    throw new Error(copy.searchUnreadable);
  const hits = value.hits.filter(
    (hit: WorkspaceFindHit) =>
      hit &&
      typeof hit.id === 'string' &&
      typeof hit.kind === 'string' &&
      typeof hit.title === 'string',
  );
  return { ...value, hits };
}

export async function listSkillsPage(
  query = '',
  offset = 0,
  limit = PAGE_SIZE,
  signal?: AbortSignal,
  locale?: string,
) {
  const copy = workspaceErrorCopy(locale);
  const params = new URLSearchParams({
    query: query.trim(),
    offset: String(offset),
    limit: String(limit),
  });
  const response = await fetch(apiUrl(`/api/v1/company/skills?${params}`), {
    credentials: 'same-origin',
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) throw new Error(copy.skillsUnavailable);
  const value = (await response.json()) as {
    skills?: unknown[];
    nextOffset?: number | null;
    hasMore?: boolean;
  };
  if (!Array.isArray(value.skills) || typeof value.hasMore !== 'boolean')
    throw new Error(copy.skillsUnreadable);
  return value as {
    skills: import('./types').CompanySkill[];
    nextOffset: number | null;
    hasMore: boolean;
  };
}

export function useSkillLibrary(open: boolean, owner: string, refreshToken?: unknown) {
  const locale = useChatInterfaceLocale();
  const [state, setState] = useState({
    owner: '',
    skills: [] as import('./types').CompanySkill[],
    nextOffset: 0 as number | null,
    hasMore: false,
    loading: false,
    error: '',
  });
  const [retryTick, setRetryTick] = useState(0);
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  useEffect(() => {
    const current = ++generation.current;
    request.current?.abort();
    if (!open || !ownerResolved(owner)) {
      setState({ owner, skills: [], nextOffset: null, hasMore: false, loading: false, error: '' });
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setState({ owner, skills: [], nextOffset: 0, hasMore: false, loading: true, error: '' });
    void listSkillsPage('', 0, SKILL_PAGE_SIZE, controller.signal, locale)
      .then((page) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState({
          owner,
          skills: page.skills,
          nextOffset: page.nextOffset,
          hasMore: page.hasMore && page.skills.length > 0,
          loading: false,
          error: '',
        });
      })
      .catch((failure) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          owner,
          loading: false,
          error: failure instanceof Error ? failure.message : 'Skills are unavailable. Try again.',
        }));
      });
    return () => {
      request.current?.abort();
      if (request.current === controller) request.current = null;
    };
  }, [open, owner, retryTick, refreshToken]);
  const loadMore = () => {
    if (state.owner !== owner || !state.hasMore || state.loading || state.nextOffset === null)
      return;
    const current = generation.current;
    const controller = new AbortController();
    request.current = controller;
    setState((previous) => ({ ...previous, owner, loading: true }));
    void listSkillsPage('', state.nextOffset, SKILL_PAGE_SIZE, controller.signal, locale)
      .then((page) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          owner,
          skills: [...previous.skills, ...page.skills],
          nextOffset: page.nextOffset,
          hasMore: page.hasMore && page.skills.length > 0,
          loading: false,
        }));
      })
      .catch((failure) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState((previous) => ({
          ...previous,
          owner,
          loading: false,
          error: failure instanceof Error ? failure.message : 'Skills are unavailable. Try again.',
        }));
      });
  };
  return {
    ...state,
    skills: state.owner === owner ? state.skills : [],
    loadMore,
    retry: () => {
      if (!state.loading) setRetryTick((value) => value + 1);
    },
  };
}

type RetrievalState = {
  key: string;
  hits: WorkspaceFindHit[];
  nextOffset: number | null;
  hasMore: boolean;
  loading: boolean;
  error: string;
  semanticStatus?: string;
  degraded?: boolean;
};
const initial: RetrievalState = {
  key: '',
  hits: [],
  nextOffset: 0,
  hasMore: false,
  loading: false,
  error: '',
};

/** Server-backed search/browse with request cancellation and company/user isolation. */
export function useWorkspaceFind(
  open: boolean,
  query: string,
  kinds: WorkspaceFindKind[],
  owner: string,
  refreshToken?: unknown,
  mode: 'hybrid' | 'keyword' = 'hybrid',
) {
  const locale = useChatInterfaceLocale();
  const key = `${owner}\0${mode}\0${kinds.join(',')}\0${query.trim()}`;
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<RetrievalState>(initial);
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  const mountedKey = useRef(key);
  mountedKey.current = key;

  useEffect(() => {
    const current = ++generation.current;
    request.current?.abort();
    request.current = null;
    if (!open || !ownerResolved(owner)) {
      setState(initial);
      return;
    }
    setState({ ...initial, key, loading: true });
    const timer = setTimeout(
      async () => {
        const controller = new AbortController();
        request.current = controller;
        try {
          const page = await findWorkspace(query, kinds, 0, controller.signal, mode, locale);
          if (
            current !== generation.current ||
            mountedKey.current !== key ||
            controller.signal.aborted
          )
            return;
          setState({
            key,
            hits: page.hits,
            nextOffset: page.nextOffset,
            hasMore: page.hasMore && page.hits.length > 0,
            loading: false,
            error: '',
            semanticStatus: page.semanticStatus,
            degraded: page.degraded,
          });
        } catch (failure) {
          if (
            current !== generation.current ||
            mountedKey.current !== key ||
            controller.signal.aborted
          )
            return;
          setState({
            ...initial,
            key,
            loading: false,
            error:
              failure instanceof Error
                ? failure.message
                : 'Workspace search is unavailable. Try again.',
          });
        } finally {
          if (request.current === controller) request.current = null;
        }
      },
      query.trim() ? 180 : 0,
    );
    return () => {
      clearTimeout(timer);
      generation.current++;
      request.current?.abort();
      request.current = null;
    };
  }, [open, key, retry, refreshToken]);

  const current =
    open && state.key === key ? state : { ...initial, loading: Boolean(open && owner) };
  const loadMore = () => {
    if (!current.hasMore || current.loading || current.nextOffset === null || !open || !owner)
      return;
    const currentGeneration = generation.current;
    const controller = new AbortController();
    request.current = controller;
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    void findWorkspace(query, kinds, current.nextOffset, controller.signal, mode, locale)
      .then((page) => {
        if (
          currentGeneration !== generation.current ||
          mountedKey.current !== key ||
          controller.signal.aborted
        )
          return;
        setState((previous) => {
          const seen = new Set(previous.hits.map((hit) => `${hit.kind}:${hit.id}`));
          const hits = [
            ...previous.hits,
            ...page.hits.filter((hit) => {
              const key = `${hit.kind}:${hit.id}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            }),
          ];
          return {
            ...previous,
            hits,
            nextOffset: page.nextOffset,
            hasMore:
              (!query.trim() || hits.length < MAX_FIND_HITS) &&
              page.hasMore &&
              page.hits.length > 0,
            loading: false,
            semanticStatus: page.semanticStatus,
            degraded: page.degraded,
          };
        });
      })
      .catch((failure) => {
        if (
          currentGeneration !== generation.current ||
          mountedKey.current !== key ||
          controller.signal.aborted
        )
          return;
        setState((previous) => ({
          ...previous,
          loading: false,
          error:
            failure instanceof Error
              ? failure.message
              : 'Workspace search is unavailable. Try again.',
        }));
      })
      .finally(() => {
        if (request.current === controller) request.current = null;
      });
  };
  return {
    ...current,
    loadMore,
    retry: () => {
      if (!current.loading) setRetry((value) => value + 1);
    },
  };
}
